// Libraries
import { useCustomPagination, DOTS } from 'hooks/useCustomPagination';
import React from 'react';
import PropTypes from 'prop-types';

// Custom hook for pagination

// Importing styles
import './tablePagination.scss';

/**
 * Represents a pagination component for a table.
 * @param {Object} props - The props object containing pagination configurations.
 * @param {Function} props.onPageChange - The function to handle page change.
 * @param {number} [props.siblingCount=1] - The number of sibling page links to display.
 * @param {number} props.currentPage - The current active page.
 * @param {string} [props.customClassName] - Custom class name for styling.
 * @param {number} props.totalPageCount - The total number of pages.
 * @param {boolean} props.isLoading - A flag indicating whether data is loading.
 * @returns {JSX.Element} A React element representing the TablePagination component.
 */
const TablePagination = props => {
    const {
        onPageChange,
        siblingCount = 1,
        currentPage,
        customClassName,
        totalPageCount,
        isLoading,
    } = props;

    // Calculate pagination range using custom hook
    const paginationRange = useCustomPagination({
        currentPage,
        siblingCount,
        totalPageCount,
    });

    // Handler function for moving to next page
    const onNext = () => {
        onPageChange(currentPage + 1);
    };

    // Handler function for moving to previous page
    const onPrevious = () => {
        onPageChange(currentPage - 1);
    };

    // Get the last page number
    const lastPage = paginationRange[paginationRange.length - 1];

    // If there's only one page or no pages, hide pagination
    if (currentPage === 0 || paginationRange.length < 2) {
        return null;
    }

    return (
        <>
            {/* Pagination container */}
            <ul className={`pagination-container ${customClassName}`}>
                {/* Previous page button */}
                <li
                    className={`pagination-item ${
                        currentPage === 1 || isLoading ? 'disabled' : ''
                    }`}
                    onClick={onPrevious}
                >
                    <div className="arrow left" />
                </li>
                {/* Render page numbers or dots */}
                {paginationRange.map((pageNumber, index) => {
                    if (pageNumber === DOTS) {
                        return (
                            <li className="pagination-item dots" key={index}>
                                &#8230;
                            </li>
                        );
                    }

                    return (
                        <li
                            className={`pagination-item
                                ${pageNumber === currentPage ? 'selected' : ''} 
                                ${isLoading ? 'disabled' : ''}
                            `}
                            key={index}
                            onClick={() => onPageChange(pageNumber)}
                        >
                            {pageNumber}
                        </li>
                    );
                })}
                {/* Next page button */}
                <li
                    className={`pagination-item ${
                        currentPage === lastPage || isLoading ? 'disabled' : ''
                    }`}
                    onClick={onNext}
                >
                    <div className="arrow right" />
                </li>
            </ul>
        </>
    );
};

// PropType validation for TablePagination component
TablePagination.propTypes = {
    onPageChange: PropTypes.func,
    siblingCount: PropTypes.number,
    currentPage: PropTypes.number,
    customClassName: PropTypes.string,
    totalPageCount: PropTypes.number,
    isLoading: PropTypes.bool,
};

export default TablePagination;
